import { useEffect, useMemo, useState } from 'react'
import {
  kExternalStorageProvider,
  kExternalStorageProviderDisplayName,
  kIsExternalStorageProvider,
  kVisibleExternalStorageProviders,
  type ExternalStorageProvider,
} from '@/constants/constants-external-storage'
import { GlobalToastType, kToastDurationShortMs } from '@/constants/constants-ui'
import getBoxAuthorizeUrl from '@/settings/fetch/get-box-authorize-url'
import getDropboxAuthorizeUrl from '@/settings/fetch/get-dropbox-authorize-url'
import getGoogleDriveAuthorizeUrl from '@/settings/fetch/get-google-drive-authorize-url'
import getOneDriveAuthorizeUrl from '@/settings/fetch/get-onedrive-authorize-url'
import deleteExternalStorageAccount from '@/settings/fetch/delete-external-storage-account'
import { pickVisibleAccount } from '@/settings/utils/pick-visible-account'
import { openGlobalToast } from '@/store/slices/global-toast.slice'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import {
  ExternalStorageActions,
  kExternalStorageRequestStatus,
  selectConnectedAccounts,
  selectExternalStorageAccountsRequest,
} from '@/user_files/external-storage/store/slice'

const PENDING_PROVIDER_STORAGE_KEY = 'paxton:dms-pending-provider'

const PROVIDER_AUTHORIZE_FETCHER: Record<ExternalStorageProvider, () => Promise<{ authorization_url: string }>> = {
  [kExternalStorageProvider.GOOGLE_DRIVE]: getGoogleDriveAuthorizeUrl,
  [kExternalStorageProvider.DROPBOX]: getDropboxAuthorizeUrl,
  [kExternalStorageProvider.BOX]: getBoxAuthorizeUrl,
  [kExternalStorageProvider.ONEDRIVE]: getOneDriveAuthorizeUrl,
}

async function getAuthorizeUrl(provider: ExternalStorageProvider): Promise<string> {
  const { authorization_url } = await PROVIDER_AUTHORIZE_FETCHER[provider]()
  return authorization_url
}

export default function useConnectedAccounts() {
  const dispatch = useAppDispatch()
  const accounts = useAppSelector(selectConnectedAccounts)
  const accountsRequest = useAppSelector(selectExternalStorageAccountsRequest)
  const [confirmingProvider, setConfirmingProvider] = useState<ExternalStorageProvider | null>(null)
  const [loadingProvider, setLoadingProvider] = useState<ExternalStorageProvider | null>(null)
  const [pendingProviderToastShown, setPendingProviderToastShown] = useState(false)

  const visibleAccount = useMemo(() => pickVisibleAccount(accounts), [accounts])
  const accountsLoadError = accountsRequest.status === kExternalStorageRequestStatus.ERROR
  const isInitialLoading = accountsRequest.status === kExternalStorageRequestStatus.IDLE || accountsRequest.status === kExternalStorageRequestStatus.LOADING

  const showToast = (type: GlobalToastType, message: string) => {
    dispatch(openGlobalToast({ type, message, durationMs: kToastDurationShortMs }))
  }

  useEffect(() => {
    if (accountsRequest.status === kExternalStorageRequestStatus.IDLE) {
      dispatch(ExternalStorageActions.fetchConnectedAccounts())
    }
  }, [accountsRequest.status, dispatch])

  useEffect(() => {
    if (accountsRequest.status !== kExternalStorageRequestStatus.SUCCESS || pendingProviderToastShown) return

    const pendingProvider = window.sessionStorage.getItem(PENDING_PROVIDER_STORAGE_KEY)
    if (!kIsExternalStorageProvider(pendingProvider)) {
      if (pendingProvider) window.sessionStorage.removeItem(PENDING_PROVIDER_STORAGE_KEY)
      return
    }

    if (accounts.some((account) => account.provider === pendingProvider && account.status === 'connected')) {
      window.sessionStorage.removeItem(PENDING_PROVIDER_STORAGE_KEY)
      showToast(GlobalToastType.SUCCESS, `${kExternalStorageProviderDisplayName[pendingProvider]} is now connected`)
      setPendingProviderToastShown(true)
    }
    // showToast dispatches, so keep this effect keyed to the stored account transition.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accounts, accountsRequest.status, pendingProviderToastShown])

  const handleProviderConnect = (provider: ExternalStorageProvider) => {
    setConfirmingProvider(provider)
  }

  const handleConfirmConnect = async () => {
    if (!confirmingProvider) return

    setLoadingProvider(confirmingProvider)
    try {
      const authorization_url = await getAuthorizeUrl(confirmingProvider)
      window.sessionStorage.setItem(PENDING_PROVIDER_STORAGE_KEY, confirmingProvider)
      window.location.href = authorization_url
    } catch {
      const providerLabel = kExternalStorageProviderDisplayName[confirmingProvider]
      window.sessionStorage.removeItem(PENDING_PROVIDER_STORAGE_KEY)
      showToast(GlobalToastType.ERROR, `${providerLabel} failed to connect. Please try again.`)
      setLoadingProvider(null)
      setConfirmingProvider(null)
    }
  }

  const handleDisconnect = async () => {
    if (!visibleAccount) return false

    setLoadingProvider(visibleAccount.provider)
    try {
      const updated = await deleteExternalStorageAccount(visibleAccount.id)
      dispatch(ExternalStorageActions.accountDisconnected({ account: updated }))
      return true
    } catch {
      showToast(GlobalToastType.ERROR, `${kExternalStorageProviderDisplayName[visibleAccount.provider]} failed to disconnect. Please try again.`)
      return false
    } finally {
      setLoadingProvider(null)
    }
  }

  const handleReconnect = () => {
    if (!visibleAccount) return

    setConfirmingProvider(visibleAccount.provider)
  }

  return {
    accountsLoadError,
    confirmingProvider,
    handleConfirmConnect,
    handleDisconnect,
    handleProviderConnect,
    handleReconnect,
    isInitialLoading,
    loadingProvider,
    providers: kVisibleExternalStorageProviders,
    setConfirmingProvider,
    visibleAccount,
  }
}
